import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import {
  SlidersHorizontal, Download, RefreshCw, X, Filter, BarChart3, TrendingUp,
  ShieldCheck, AlertTriangle, Users, IndianRupee, Clock, ArrowUpDown,
  Search, Eye, ChevronLeft, ChevronRight, FileSpreadsheet, Printer, CheckCircle2,
  XCircle, Clock3, ExternalLink
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/lib/api';
import { formatCurrency, formatNumber, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

// ── Workflow Classification & Governance ──────────────────────────────────
export type WorkflowStage =
  | 'DRAFT'
  | 'PENDING_PM'
  | 'PENDING_CH'
  | 'APPROVED'
  | 'REJECTED_PM'
  | 'REJECTED_CH'
  | 'UNKNOWN';

export function classifyRequirementWorkflow(statusOrRecord?: any, pmDecision?: string, chDecision?: string): WorkflowStage {
  let st = '';
  let pm = '';
  let ch = '';

  if (typeof statusOrRecord === 'object' && statusOrRecord !== null) {
    st = String(statusOrRecord.requirement_status ?? statusOrRecord.status ?? '').toUpperCase().trim();
    pm = String(statusOrRecord.pm_approval ?? statusOrRecord.pm_decision ?? statusOrRecord.manager_decision ?? '').toUpperCase().trim();
    ch = String(statusOrRecord.ch_approval ?? statusOrRecord.ch_decision ?? statusOrRecord.cluster_decision ?? '').toUpperCase().trim();
  } else {
    st = String(statusOrRecord || '').toUpperCase().trim();
    pm = String(pmDecision || '').toUpperCase().trim();
    ch = String(chDecision || '').toUpperCase().trim();
  }

  // 1. Rejections take precedence (PM rejection or Cluster Head rejection)
  if (st === 'REJECTED_BY_MANAGER' || pm === 'REJECTED') {
    return 'REJECTED_PM';
  }
  if (st === 'REJECTED_BY_CLUSTER_HEAD' || ch === 'REJECTED') {
    return 'REJECTED_CH';
  }
  if (st.includes('REJECTED')) {
    return 'REJECTED_PM';
  }

  // 2. Draft requisitions are not awaiting review
  if (st === 'DRAFT') {
    return 'DRAFT';
  }

  // 3. Fully Approved (both PM and CH sign-offs completed)
  if (st === 'APPROVED' || (pm === 'APPROVED' && ch === 'APPROVED')) {
    return 'APPROVED';
  }

  // 4. Pending Cluster Head (PM approved, awaiting final sign-off)
  if (st === 'MANAGER_APPROVED' || (pm === 'APPROVED' && ch === 'PENDING')) {
    return 'PENDING_CH';
  }

  // 5. Pending Project Manager (Submitted, awaiting manager review)
  if (st === 'PENDING_MANAGER_APPROVAL' || st === 'SUBMITTED' || pm === 'PENDING') {
    return 'PENDING_PM';
  }

  return 'UNKNOWN';
}

// ── Color Palette (Enterprise Analytics Palette) ───────────────────────────
const STATUS_COLORS: Record<string, string> = {
  APPROVED: '#10b981',
  MANAGER_APPROVED: '#0d9488',
  PENDING_MANAGER_APPROVAL: '#f59e0b',
  REJECTED_BY_MANAGER: '#f43f5e',
  REJECTED_BY_CLUSTER_HEAD: '#e11d48',
  DRAFT: '#94a3b8',
};

const SKILL_COLORS: Record<string, string> = {
  SKILLED: '#0d9488',
  SEMI_SKILLED: '#3b82f6',
  UNSKILLED: '#f59e0b',
  UNCLASSIFIED: '#94a3b8',
};

interface FilterState {
  projectId: string;
  dateFrom: string;
  dateTo: string;
  blockId: string;
  activityId: string;
  workerCategoryId: string;
  skillLevel: string;
  shiftId: string;
  status: string;
  managerDecision: string;
  clusterDecision: string;
}

const INITIAL_FILTERS: FilterState = {
  projectId: '',
  dateFrom: '',
  dateTo: '',
  blockId: '',
  activityId: '',
  workerCategoryId: '',
  skillLevel: '',
  shiftId: '',
  status: '',
  managerDecision: '',
  clusterDecision: '',
};

const ALL_COLUMNS = [
  { key: 'requirement_number', label: 'Requirement Ref #', defaultVisible: true },
  { key: 'project_name', label: 'Project', defaultVisible: true },
  { key: 'work_date', label: 'Work Date', defaultVisible: true },
  { key: 'block_name', label: 'Block', defaultVisible: true },
  { key: 'activity_name', label: 'Activity', defaultVisible: true },
  { key: 'unit_name', label: 'Unit', defaultVisible: true },
  { key: 'quantity', label: 'Quantity', defaultVisible: true },
  { key: 'work_location', label: 'Work Location', defaultVisible: false },
  { key: 'worker_category_name', label: 'Worker Category', defaultVisible: true },
  { key: 'skill_level', label: 'Skill Level', defaultVisible: true },
  { key: 'required_headcount', label: 'Headcount', defaultVisible: true },
  { key: 'planned_hours', label: 'Hours', defaultVisible: true },
  { key: 'planned_rate', label: 'Daily Rate (₹/day)', defaultVisible: true },
  { key: 'planned_amount', label: 'Total Amount (₹)', defaultVisible: true },
  { key: 'shift_name', label: 'Shift', defaultVisible: true },
  { key: 'priority', label: 'Priority', defaultVisible: true },
  { key: 'assigned_pm_name', label: 'Assigned PM', defaultVisible: false },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'manager_decision', label: 'PM Approval', defaultVisible: true },
  { key: 'manager_remarks', label: 'PM Remarks', defaultVisible: false },
  { key: 'cluster_decision', label: 'CH Approval', defaultVisible: true },
  { key: 'cluster_remarks', label: 'CH Remarks', defaultVisible: false },
];

export default function LabourAnalyticsPage() {
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Dark / Light Theme Resolution
  let themeMode = 'light';
  try {
    const themeCtx = useTheme();
    if (themeCtx?.theme) themeMode = themeCtx.theme;
  } catch {
    if (typeof document !== 'undefined') {
      themeMode = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    }
  }
  const isDark = themeMode === 'dark';

  const chartTheme = useMemo(() => ({
    gridStroke: isDark ? '#233751' : '#e2e8f0',
    axisTick: isDark ? '#93a1b8' : '#64748b',
    axisLabel: isDark ? '#f5f8ff' : '#0f172a',
    tooltip: {
      backgroundColor: isDark ? '#101d2f' : '#ffffff',
      borderColor: isDark ? '#344a67' : '#cbd5e1',
      color: isDark ? '#f5f8ff' : '#081631',
      borderRadius: 8,
      boxShadow: isDark ? '0 10px 25px rgba(0,0,0,0.5)' : '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    },
    itemStyle: {
      color: isDark ? '#f5f8ff' : '#081631',
    },
  }), [isDark]);

  // Filters State
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Table State
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<string>('work_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    new Set(ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key))
  );
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  // Date validation
  const isDateInvalid = Boolean(filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo);

  // Master Data Dropdowns
  const { data: projects = [] } = useQuery({
    queryKey: ['analytics-projects'],
    queryFn: async () => {
      const res = await api.get<{ data: any[] }>('/projects?availability=active');
      return res.data ?? [];
    },
  });

  const { data: blocks = [] } = useQuery({
    queryKey: ['analytics-blocks', filters.projectId],
    queryFn: async () => {
      const q = filters.projectId ? `?projectId=${encodeURIComponent(filters.projectId)}` : '';
      const res = await api.get<{ data: any[] }>(`/blocks/all${q}`);
      return res.data ?? [];
    },
  });

  const { data: activities = [] } = useQuery({
    queryKey: ['analytics-activities', filters.projectId, filters.blockId],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (filters.projectId) q.set('projectId', filters.projectId);
      if (filters.blockId) q.set('blockId', filters.blockId);
      const res = await api.get<{ data: any[] }>(`/activities/all?${q.toString()}`);
      return res.data ?? [];
    },
  });

  const { data: workerCategories = [] } = useQuery({
    queryKey: ['analytics-categories'],
    queryFn: async () => {
      const res = await api.get<{ data: any[] }>('/labour-categories');
      return res.data ?? [];
    },
  });

  const { data: shifts = [] } = useQuery({
    queryKey: ['analytics-shifts'],
    queryFn: async () => {
      const res = await api.get<{ data: any[] }>('/shifts');
      return res.data ?? [];
    },
  });

  // Query String Builder for Authoritative Custom Builder Backend
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('reportType', 'labour_requirement');
    p.set('columns', ALL_COLUMNS.map((c) => c.key).join(','));
    if (filters.projectId) p.set('projectId', filters.projectId);
    if (filters.dateFrom) p.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) p.set('dateTo', filters.dateTo);
    if (filters.blockId) p.set('blockId', filters.blockId);
    if (filters.activityId) p.set('activityId', filters.activityId);
    if (filters.workerCategoryId) p.set('workerCategoryId', filters.workerCategoryId);
    if (filters.skillLevel) p.set('skillLevel', filters.skillLevel);
    if (filters.shiftId) p.set('shiftId', filters.shiftId);
    if (filters.status) p.set('status', filters.status);
    if (filters.managerDecision) p.set('managerDecision', filters.managerDecision);
    if (filters.clusterDecision) p.set('clusterDecision', filters.clusterDecision);
    return p.toString();
  }, [filters]);

  // Main Analytics Data Fetch
  const {
    data: reportData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['labour-analytics-dataset', queryString],
    queryFn: async () => {
      if (isDateInvalid) throw new Error('Start date cannot be after end date.');
      const res = await api.get<{
        success: boolean;
        data: {
          rows: any[];
          totals: Record<string, number>;
          totalRecords: number;
        };
      }>(`/reports/custom-builder?${queryString}`);
      return res.data;
    },
    enabled: !isDateInvalid,
  });

  const rawRows: any[] = reportData?.rows ?? [];

  // ── Executive KPI Calculations (Deduplicated vs Row Counts) ───────────────
  const metrics = useMemo(() => {
    const totalRecords = rawRows.length; // Row count / line-item count
    const uniqueReqIds = new Set<string>();
    const uniqueProjects = new Set<string>();
    let totalHeadcount = 0;
    let totalAmount = 0;

    // Track requirement-level statuses (deduplicated by requirement_id)
    const reqStatusMap = new Map<string, { status: string; pm: string; ch: string; project: string }>();

    for (const r of rawRows) {
      const reqId = r.requirement_id || r.requirement_number;
      if (reqId) {
        uniqueReqIds.add(reqId);
        if (!reqStatusMap.has(reqId)) {
          reqStatusMap.set(reqId, {
            status: r.requirement_status || r.status || '',
            pm: r.manager_decision || (typeof r.pm_approval === 'string' ? r.pm_approval.toUpperCase() : ''),
            ch: r.cluster_decision || (typeof r.ch_approval === 'string' ? r.ch_approval.toUpperCase() : ''),
            project: r.project_name,
          });
        }
      }
      if (r.project_name && r.project_name !== '-') {
        uniqueProjects.add(r.project_name);
      }
      totalHeadcount += Number(r.required_headcount) || 0;
      totalAmount += Number(r.planned_amount) || 0;
    }

    const uniqueRequirements = uniqueReqIds.size;
    const projectCount = uniqueProjects.size;

    let pendingPmCount = 0;
    let pendingChCount = 0;
    let approvedCount = 0;
    let rejectedCount = 0;
    let rejectedPmCount = 0;
    let rejectedChCount = 0;
    let draftCount = 0;

    for (const item of reqStatusMap.values()) {
      const stage = classifyRequirementWorkflow(item.status, item.pm, item.ch);
      switch (stage) {
        case 'PENDING_PM':
          pendingPmCount++;
          break;
        case 'PENDING_CH':
          pendingChCount++;
          break;
        case 'APPROVED':
          approvedCount++;
          break;
        case 'REJECTED_PM':
          rejectedCount++;
          rejectedPmCount++;
          break;
        case 'REJECTED_CH':
          rejectedCount++;
          rejectedChCount++;
          break;
        case 'DRAFT':
          draftCount++;
          break;
      }
    }

    return {
      totalRecords,
      uniqueRequirements,
      projectCount,
      totalHeadcount,
      totalAmount,
      pendingPmCount,
      pendingChCount,
      approvedCount,
      rejectedCount,
      rejectedPmCount,
      rejectedChCount,
      draftCount,
    };
  }, [rawRows]);

  // ── Chart Aggregations ────────────────────────────────────────────────────
  // 1. Project Spend & Headcount Ranking
  const projectChartData = useMemo(() => {
    const map = new Map<string, { name: string; amount: number; headcount: number; requirements: Set<string> }>();
    for (const r of rawRows) {
      const p = r.project_name || 'Unassigned';
      if (!map.has(p)) map.set(p, { name: p, amount: 0, headcount: 0, requirements: new Set() });
      const entry = map.get(p)!;
      entry.amount += Number(r.planned_amount) || 0;
      entry.headcount += Number(r.required_headcount) || 0;
      if (r.requirement_id || r.requirement_number) {
        entry.requirements.add(r.requirement_id || r.requirement_number);
      }
    }
    return Array.from(map.values())
      .map((e) => ({ ...e, count: e.requirements.size }))
      .sort((a, b) => b.amount - a.amount);
  }, [rawRows]);

  // 2. Requirement Status Distribution
  const statusDistributionData = useMemo(() => {
    const counts: Record<string, number> = {};
    const seenReqs = new Set<string>();
    for (const r of rawRows) {
      const reqKey = r.requirement_id || r.requirement_number;
      if (reqKey && seenReqs.has(reqKey)) continue;
      if (reqKey) seenReqs.add(reqKey);

      const st = String(r.requirement_status || r.status || 'UNKNOWN').toUpperCase();
      counts[st] = (counts[st] || 0) + 1;
    }
    return Object.entries(counts).map(([status, count]) => ({
      name: status.replace(/_/g, ' '),
      rawStatus: status,
      value: count,
      color: STATUS_COLORS[status] || '#64748b',
    }));
  }, [rawRows]);

  // 3. Approval Decision Stage Comparisons (PM vs CH)
  const approvalComparisonData = useMemo(() => {
    let pmApproved = 0, pmRejected = 0, pmPending = 0, pmDraft = 0;
    let chApproved = 0, chRejected = 0, chPending = 0, chNotReached = 0;
    const seenReqs = new Set<string>();

    for (const r of rawRows) {
      const reqKey = r.requirement_id || r.requirement_number;
      if (reqKey && seenReqs.has(reqKey)) continue;
      if (reqKey) seenReqs.add(reqKey);

      // PM stage
      const pm = String(r.manager_decision || r.pm_approval || '').toUpperCase();
      if (pm === 'APPROVED') pmApproved++;
      else if (pm === 'REJECTED') pmRejected++;
      else if (pm === 'PENDING') pmPending++;
      else pmDraft++;

      // CH stage
      const ch = String(r.cluster_decision || r.ch_approval || '').toUpperCase();
      if (ch === 'APPROVED') chApproved++;
      else if (ch === 'REJECTED') chRejected++;
      else if (ch === 'PENDING') chPending++;
      else chNotReached++;
    }

    return [
      { stage: 'Approved', PM: pmApproved, CH: chApproved },
      { stage: 'Pending', PM: pmPending, CH: chPending },
      { stage: 'Rejected', PM: pmRejected, CH: chRejected },
      { stage: 'Draft / Unreached', PM: pmDraft, CH: chNotReached },
    ];
  }, [rawRows]);

  // 4. Pending Approvals by Project
  const pendingByProjectData = useMemo(() => {
    const map = new Map<string, { project: string; pendingPm: number; pendingCh: number }>();
    const seenReqs = new Set<string>();

    for (const r of rawRows) {
      const reqKey = r.requirement_id || r.requirement_number;
      if (reqKey && seenReqs.has(reqKey)) continue;
      if (reqKey) seenReqs.add(reqKey);

      const p = r.project_name || 'Unassigned';
      if (!map.has(p)) map.set(p, { project: p, pendingPm: 0, pendingCh: 0 });
      const entry = map.get(p)!;

      const stage = classifyRequirementWorkflow(r.requirement_status || r.status, r.manager_decision || r.pm_approval, r.cluster_decision || r.ch_approval);
      if (stage === 'PENDING_PM') {
        entry.pendingPm++;
      } else if (stage === 'PENDING_CH') {
        entry.pendingCh++;
      }
    }

    return Array.from(map.values())
      .filter((e) => e.pendingPm > 0 || e.pendingCh > 0)
      .sort((a, b) => b.pendingPm + b.pendingCh - (a.pendingPm + a.pendingCh));
  }, [rawRows]);

  // 5. Work Date Timeline (Expenditure & Headcount Trend)
  const timelineData = useMemo(() => {
    const map = new Map<string, { date: string; amount: number; headcount: number }>();
    for (const r of rawRows) {
      const d = r.work_date;
      if (!d || d === '-') continue;
      if (!map.has(d)) map.set(d, { date: d, amount: 0, headcount: 0 });
      const entry = map.get(d)!;
      entry.amount += Number(r.planned_amount) || 0;
      entry.headcount += Number(r.required_headcount) || 0;
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [rawRows]);

  // 6. Skill Level Distribution
  const skillDistributionData = useMemo(() => {
    const map = new Map<string, { name: string; rawSkill: string; headcount: number; amount: number }>();
    for (const r of rawRows) {
      const s = r.skill_level || 'UNCLASSIFIED';
      if (!map.has(s)) {
        map.set(s, {
          name: s === 'SEMI_SKILLED' ? 'Semi-Skilled' : s === 'SKILLED' ? 'Skilled' : s === 'UNSKILLED' ? 'Unskilled' : s,
          rawSkill: s,
          headcount: 0,
          amount: 0,
        });
      }
      const entry = map.get(s)!;
      entry.headcount += Number(r.required_headcount) || 0;
      entry.amount += Number(r.planned_amount) || 0;
    }
    return Array.from(map.values()).map((e) => ({
      ...e,
      color: SKILL_COLORS[e.rawSkill] || '#64748b',
    }));
  }, [rawRows]);

  // 7. Activity Expenditure Ranking
  const activitySpendData = useMemo(() => {
    const map = new Map<string, { activity: string; amount: number; headcount: number }>();
    for (const r of rawRows) {
      const a = r.activity_name || 'Unassigned';
      if (!map.has(a)) map.set(a, { activity: a, amount: 0, headcount: 0 });
      const entry = map.get(a)!;
      entry.amount += Number(r.planned_amount) || 0;
      entry.headcount += Number(r.required_headcount) || 0;
    }
    return Array.from(map.values())
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8); // Top 8 activities
  }, [rawRows]);

  // 8. Shift Headcount Distribution
  const shiftData = useMemo(() => {
    const map = new Map<string, { shift: string; headcount: number; amount: number }>();
    for (const r of rawRows) {
      const s = r.shift_name || 'Unspecified';
      if (!map.has(s)) map.set(s, { shift: s, headcount: 0, amount: 0 });
      const entry = map.get(s)!;
      entry.headcount += Number(r.required_headcount) || 0;
      entry.amount += Number(r.planned_amount) || 0;
    }
    return Array.from(map.values()).sort((a, b) => b.headcount - a.headcount);
  }, [rawRows]);

  // 9. Priority Distribution
  const priorityData = useMemo(() => {
    const map = new Map<string, number>();
    const seenReqs = new Set<string>();
    for (const r of rawRows) {
      const reqKey = r.requirement_id || r.requirement_number;
      if (reqKey && seenReqs.has(reqKey)) continue;
      if (reqKey) seenReqs.add(reqKey);

      const p = (r.priority || 'NORMAL').toUpperCase();
      map.set(p, (map.get(p) || 0) + 1);
    }
    return Array.from(map.entries()).map(([priority, count]) => ({
      priority,
      count,
    }));
  }, [rawRows]);

  // ── Interactive Cross-Filtering Helpers ───────────────────────────────────
  const applyCrossFilter = (field: keyof FilterState, value: string) => {
    setFilters((prev) => {
      // Toggle off if already active
      if (prev[field] === value) {
        return { ...prev, [field]: '' };
      }
      return { ...prev, [field]: value };
    });
    setPage(1);
    addToast(`Filter applied: ${String(field)} = ${value}`, 'info');
  };

  const handleResetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSearchQuery('');
    setPage(1);
    addToast('All filters have been reset to default', 'info');
  };

  const removeSingleFilter = (field: keyof FilterState) => {
    setFilters((prev) => ({ ...prev, [field]: '' }));
    setPage(1);
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(Boolean).length;
  }, [filters]);

  // ── Detailed Table Filter & Sort & Pagination ─────────────────────────────
  const filteredTableRows = useMemo(() => {
    let list = [...rawRows];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((r) =>
        ['requirement_number', 'project_name', 'activity_name', 'worker_category_name', 'assigned_pm_name', 'status', 'block_name', 'work_location']
          .some((k) => String(r[k] || '').toLowerCase().includes(q))
      );
    }

    list.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === bv) return 0;
      if (av === null || av === undefined || av === '-') return 1;
      if (bv === null || bv === undefined || bv === '-') return -1;

      const mult = sortDir === 'asc' ? 1 : -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * mult;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * mult;
    });

    return list;
  }, [rawRows, searchQuery, sortKey, sortDir]);

  const totalTablePages = Math.max(1, Math.ceil(filteredTableRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredTableRows.slice(start, start + pageSize);
  }, [filteredTableRows, page, pageSize]);

  // Export CSV Action
  const handleExportCsv = async () => {
    try {
      setExportingCsv(true);
      const csvUrl = `/api/v1/reports/custom-builder?${queryString}&format=excel`;
      const token = localStorage.getItem('token');
      const res = await fetch(csvUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Failed to download CSV');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `labour_analytics_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
      addToast('Analytics dataset exported to CSV successfully', 'success');
    } catch (err: any) {
      addToast(err.message || 'Export failed', 'error');
    } finally {
      setExportingCsv(false);
    }
  };

  const canView = hasPermission('reports:view');
  if (!canView) {
    return (
      <div className="p-12 text-center bg-surface border border-default rounded-xl shadow-sm">
        <h2 className="text-xl font-bold text-primary mb-2">Access Denied</h2>
        <p className="text-sm text-secondary">You do not have permission to view Labour Analytics reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 font-sans antialiased text-primary">
      {/* Error state notification */}
      {isError && (
        <div role="alert" className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 flex items-center justify-between text-rose-700 dark:text-rose-300 text-sm">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span>Failed to load authoritative analytics data: {(error as any)?.message || 'Query error'}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="border-rose-300 dark:border-rose-800 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      )}

      {/* ── 1. Executive Analytics Header Bar ───────────────────────────────── */}
      <div className="bg-surface text-primary p-5 sm:p-6 rounded-2xl shadow-sm border border-default relative overflow-hidden transition-colors">
        {/* Subtle decorative background glow that blends with light & dark theme */}
        <div className="absolute -right-16 -top-16 w-80 h-80 bg-teal-500/5 dark:bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute right-60 -bottom-20 w-60 h-60 bg-blue-500/5 dark:bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-50 text-brand-600 dark:text-brand-300 border border-brand-200 dark:border-brand-800 uppercase tracking-wider">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse" />
                Departmental Labour App
              </span>
              <span className="text-xs text-muted">
                Swayam Solutions
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-primary flex items-center gap-2.5">
              <TrendingUp className="h-7 w-7 text-brand-600 dark:text-brand-400" />
              Labour Analytics Dashboard
            </h1>
            <p className="text-sm text-secondary mt-1 max-w-2xl">
              Labour requirements, workforce allocation, costs, and approval insights
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="border-default bg-surface hover:bg-surface-secondary text-secondary hover:text-primary transition-colors"
              title="Refresh dashboard data"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin text-brand-600 dark:text-brand-400' : ''}`} />
              {isFetching ? 'Updating...' : 'Refresh'}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={exportingCsv || rawRows.length === 0}
              className="border-default bg-surface hover:bg-surface-secondary text-secondary hover:text-primary transition-colors"
            >
              <Download className="h-4 w-4 mr-1.5" />
              {exportingCsv ? 'Exporting...' : 'Export CSV'}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              className="border-default bg-surface hover:bg-surface-secondary text-secondary hover:text-primary hidden sm:inline-flex transition-colors"
            >
              <Printer className="h-4 w-4 mr-1.5" />
              Print
            </Button>

            <Link
              to="/reports/custom"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors bg-brand-500 hover:bg-brand-600 text-white h-9 px-3.5 shadow-sm"
            >
              <SlidersHorizontal className="h-4 w-4 mr-1.5" />
              Custom Report Builder
            </Link>
          </div>
        </div>

        {/* Status Bar */}
        <div className="mt-5 pt-4 border-t border-default/70 flex flex-wrap items-center justify-between gap-3 text-xs text-secondary">
          <div className="flex items-center gap-3">
            <span>
              Last refreshed:{' '}
              <strong className="text-primary font-medium">
                {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : 'Initial load'}
              </strong>
            </span>
            <span>·</span>
            <span>
              Data Source:{' '}
              <strong className="text-teal-600 dark:text-teal-400 font-medium">Authoritative Database Records</strong>
            </span>
            <span>·</span>
            <span>
              Rows Loaded:{' '}
              <strong className="text-primary font-medium">{formatNumber(metrics.totalRecords)}</strong>
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilterDrawer((prev) => !prev)}
              className="inline-flex items-center gap-1 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium transition-colors"
            >
              <Filter className="h-3.5 w-3.5" />
              {showFilterDrawer ? 'Hide Global Slicers' : 'Show Global Slicers'}
              {activeFilterCount > 0 && (
                <span className="ml-1 px-1.5 py-0.2 rounded-full bg-teal-600 dark:bg-teal-500 text-white font-bold text-[10px]">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── 2. Global Slicer Filter Bar ──────────────────────────────────── */}
      <Card className="border-default bg-surface shadow-sm overflow-hidden transition-all duration-200">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3.5">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              <h3 className="text-sm font-bold tracking-tight text-primary uppercase">
                Global Slicer Filters
              </h3>
              {activeFilterCount > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-500/10 text-teal-700 dark:text-teal-300 border border-teal-500/30">
                  {activeFilterCount} active
                </span>
              )}
            </div>

            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 h-7 px-2.5"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                Reset All Filters
              </Button>
            )}
          </div>

          {/* Slicer Controls Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {/* Project */}
            <div>
              <Label htmlFor="filter-project" className="text-xs font-semibold text-secondary mb-1 block">Project</Label>
              <Select
                value={filters.projectId || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({
                    ...prev,
                    projectId: v === 'all' ? '' : v,
                    blockId: '', // Reset cascading block & activity
                    activityId: '',
                  }));
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-project" aria-label="Project slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder="All Permitted Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Permitted Projects</SelectItem>
                  {projects.map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <Label htmlFor="filter-date-from" className="text-xs font-semibold text-secondary mb-1 block">Start Date</Label>
              <DatePicker
                id="filter-date-from"
                aria-label="Start work date"
                value={filters.dateFrom}
                onChange={(d: any) => {
                  const val = typeof d === 'string' ? d : d?.target?.value ?? '';
                  setFilters((prev) => ({ ...prev, dateFrom: val }));
                  setPage(1);
                }}
                className="h-8 text-xs w-full bg-surface border-default text-primary"
                placeholder="From Date"
              />
            </div>

            {/* End Date */}
            <div>
              <Label htmlFor="filter-date-to" className="text-xs font-semibold text-secondary mb-1 block">End Date</Label>
              <DatePicker
                id="filter-date-to"
                aria-label="End work date"
                value={filters.dateTo}
                onChange={(d: any) => {
                  const val = typeof d === 'string' ? d : d?.target?.value ?? '';
                  setFilters((prev) => ({ ...prev, dateTo: val }));
                  setPage(1);
                }}
                className="h-8 text-xs w-full bg-surface border-default text-primary"
                placeholder="To Date"
              />
            </div>

            {/* Block (Cascades with Project) */}
            <div>
              <Label htmlFor="filter-block" className="text-xs font-semibold text-secondary mb-1 block">Block</Label>
              <Select
                value={filters.blockId || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({
                    ...prev,
                    blockId: v === 'all' ? '' : v,
                    activityId: '', // Reset activity
                  }));
                  setPage(1);
                }}
                disabled={blocks.length === 0}
              >
                <SelectTrigger id="filter-block" aria-label="Block slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder={blocks.length === 0 ? 'No Blocks' : 'All Blocks'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Blocks</SelectItem>
                  {blocks.map((b: any) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Activity */}
            <div>
              <Label htmlFor="filter-activity" className="text-xs font-semibold text-secondary mb-1 block">Activity</Label>
              <Select
                value={filters.activityId || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, activityId: v === 'all' ? '' : v }));
                  setPage(1);
                }}
                disabled={activities.length === 0}
              >
                <SelectTrigger id="filter-activity" aria-label="Activity slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder={activities.length === 0 ? 'No Activities' : 'All Activities'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activities</SelectItem>
                  {activities.map((a: any) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Worker Category */}
            <div>
              <Label htmlFor="filter-category" className="text-xs font-semibold text-secondary mb-1 block">Worker Category</Label>
              <Select
                value={filters.workerCategoryId || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, workerCategoryId: v === 'all' ? '' : v }));
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-category" aria-label="Category slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {workerCategories.map((c: any) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Skill Level */}
            <div>
              <Label htmlFor="filter-skill" className="text-xs font-semibold text-secondary mb-1 block">Skill Level</Label>
              <Select
                value={filters.skillLevel || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, skillLevel: v === 'all' ? '' : v }));
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-skill" aria-label="Skill level slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder="All Skill Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Skill Levels</SelectItem>
                  <SelectItem value="SKILLED">Skilled</SelectItem>
                  <SelectItem value="SEMI_SKILLED">Semi-Skilled</SelectItem>
                  <SelectItem value="UNSKILLED">Unskilled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Shift */}
            <div>
              <Label htmlFor="filter-shift" className="text-xs font-semibold text-secondary mb-1 block">Shift</Label>
              <Select
                value={filters.shiftId || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, shiftId: v === 'all' ? '' : v }));
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-shift" aria-label="Shift slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder="All Shifts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Shifts</SelectItem>
                  {shifts.map((s: any) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Requirement Status */}
            <div>
              <Label htmlFor="filter-status" className="text-xs font-semibold text-secondary mb-1 block">Requirement Status</Label>
              <Select
                value={filters.status || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, status: v === 'all' ? '' : v }));
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-status" aria-label="Requirement status slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="PENDING_MANAGER_APPROVAL">Pending Manager Approval</SelectItem>
                  <SelectItem value="MANAGER_APPROVED">Manager Approved</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="REJECTED_BY_MANAGER">Rejected by Manager</SelectItem>
                  <SelectItem value="REJECTED_BY_CLUSTER_HEAD">Rejected by Cluster Head</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* PM Approval Status */}
            <div>
              <Label htmlFor="filter-pm-approval" className="text-xs font-semibold text-secondary mb-1 block">PM Approval Status</Label>
              <Select
                value={filters.managerDecision || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, managerDecision: v === 'all' ? '' : v }));
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-pm-approval" aria-label="PM approval slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder="All PM Decisions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All PM Decisions</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* CH Approval Status */}
            <div>
              <Label htmlFor="filter-ch-approval" className="text-xs font-semibold text-secondary mb-1 block">CH Approval Status</Label>
              <Select
                value={filters.clusterDecision || 'all'}
                onValueChange={(v) => {
                  setFilters((prev) => ({ ...prev, clusterDecision: v === 'all' ? '' : v }));
                  setPage(1);
                }}
              >
                <SelectTrigger id="filter-ch-approval" aria-label="CH approval slicer" className="h-8 text-xs bg-surface border-default text-primary hover:border-brand-500">
                  <SelectValue placeholder="All CH Decisions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All CH Decisions</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date Validation Alert */}
          {isDateInvalid && (
            <div className="mt-3 p-2.5 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 flex items-center gap-2 text-rose-700 dark:text-rose-300 text-xs">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Invalid date range: Start date ({filters.dateFrom}) cannot be after End date ({filters.dateTo}).</span>
            </div>
          )}

          {/* Active Filter Pill Tags */}
          {activeFilterCount > 0 && (
            <div className="mt-3.5 pt-3 border-t border-default/60 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold text-muted mr-1">Active Slicers:</span>
              {filters.projectId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  Project: {projects.find((p: any) => p.id === filters.projectId)?.name || filters.projectId}
                  <button onClick={() => removeSingleFilter('projectId')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.dateFrom && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  From: {filters.dateFrom}
                  <button onClick={() => removeSingleFilter('dateFrom')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.dateTo && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  To: {filters.dateTo}
                  <button onClick={() => removeSingleFilter('dateTo')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.blockId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  Block: {blocks.find((b: any) => b.id === filters.blockId)?.name || filters.blockId}
                  <button onClick={() => removeSingleFilter('blockId')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.activityId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  Activity: {activities.find((a: any) => a.id === filters.activityId)?.name || filters.activityId}
                  <button onClick={() => removeSingleFilter('activityId')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.workerCategoryId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  Category: {workerCategories.find((c: any) => c.id === filters.workerCategoryId)?.name || filters.workerCategoryId}
                  <button onClick={() => removeSingleFilter('workerCategoryId')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.skillLevel && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  Skill: {filters.skillLevel}
                  <button onClick={() => removeSingleFilter('skillLevel')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.shiftId && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  Shift: {shifts.find((s: any) => s.id === filters.shiftId)?.name || filters.shiftId}
                  <button onClick={() => removeSingleFilter('shiftId')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.status && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  Status: {filters.status}
                  <button onClick={() => removeSingleFilter('status')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.managerDecision && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  PM: {filters.managerDecision}
                  <button onClick={() => removeSingleFilter('managerDecision')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
              {filters.clusterDecision && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-secondary text-secondary border border-default">
                  CH: {filters.clusterDecision}
                  <button onClick={() => removeSingleFilter('clusterDecision')}><X className="h-3 w-3 hover:text-rose-600 dark:hover:text-rose-400" /></button>
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── 3. Executive KPI Cards ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {/* KPI 1: Total Labour Amount */}
        <Card className="bg-surface border-default shadow-sm hover:shadow transition-shadow">
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between text-secondary mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider">Total Amount</span>
              <IndianRupee className="h-4 w-4 text-teal-600 dark:text-teal-400" />
            </div>
            <div data-testid="kpi-total-amount" className="text-lg font-extrabold text-primary truncate" title={isError ? 'Error loading data' : formatCurrency(metrics.totalAmount)}>
              {isError ? '—' : formatCurrency(metrics.totalAmount)}
            </div>
            <div className="text-[10px] text-muted mt-0.5 truncate">
              Planned expenditure
            </div>
          </CardContent>
        </Card>

        {/* KPI 2: Total Headcount */}
        <Card className="bg-surface border-default shadow-sm hover:shadow transition-shadow">
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between text-secondary mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider">Total Headcount</span>
              <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div data-testid="kpi-total-headcount" className="text-lg font-extrabold text-primary">
              {isError ? '—' : formatNumber(metrics.totalHeadcount)}
            </div>
            <div className="text-[10px] text-muted mt-0.5">
              Requested workers
            </div>
          </CardContent>
        </Card>

        {/* KPI 3: Unique Requirements */}
        <Card className="bg-surface border-default shadow-sm hover:shadow transition-shadow">
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between text-secondary mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider">Unique Reqs</span>
              <FileSpreadsheet className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div data-testid="kpi-unique-requirements" className="text-lg font-extrabold text-primary">
              {isError ? '—' : formatNumber(metrics.uniqueRequirements)}
            </div>
            <div className="text-[10px] text-muted mt-0.5">
              Distinct requests
            </div>
          </CardContent>
        </Card>

        {/* KPI 4: Line Items */}
        <Card className="bg-surface border-default shadow-sm hover:shadow transition-shadow">
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between text-secondary mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider">Line Items</span>
              <BarChart3 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div data-testid="kpi-total-records" className="text-lg font-extrabold text-primary">
              {isError ? '—' : formatNumber(metrics.totalRecords)}
            </div>
            <div className="text-[10px] text-muted mt-0.5">
              Trade assignments
            </div>
          </CardContent>
        </Card>

        {/* KPI 5: Pending PM */}
        <Card
          className="bg-surface border-amber-300 dark:border-amber-900/60 shadow-sm hover:shadow transition-shadow cursor-pointer hover:bg-amber-50/40 dark:hover:bg-amber-950/25"
          onClick={() => applyCrossFilter('status', 'PENDING_MANAGER_APPROVAL')}
          title="Click to isolate Pending PM requirements"
        >
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between text-amber-700 dark:text-amber-400 mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider">Pending PM</span>
              <Clock3 className="h-4 w-4 text-amber-500" />
            </div>
            <div data-testid="kpi-pending-pm" className="text-lg font-extrabold text-amber-900 dark:text-amber-300">
              {isError ? '—' : formatNumber(metrics.pendingPmCount)}
            </div>
            <div className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5 font-medium">
              Awaiting Manager
            </div>
          </CardContent>
        </Card>

        {/* KPI 6: Pending CH */}
        <Card
          className="bg-surface border-teal-300 dark:border-teal-900/60 shadow-sm hover:shadow transition-shadow cursor-pointer hover:bg-teal-50/40 dark:hover:bg-teal-950/25"
          onClick={() => applyCrossFilter('status', 'MANAGER_APPROVED')}
          title="Click to isolate Pending Cluster Head requirements"
        >
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between text-teal-700 dark:text-teal-400 mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider">Pending CH</span>
              <Clock className="h-4 w-4 text-teal-500" />
            </div>
            <div data-testid="kpi-pending-ch" className="text-lg font-extrabold text-teal-900 dark:text-teal-300">
              {isError ? '—' : formatNumber(metrics.pendingChCount)}
            </div>
            <div className="text-[10px] text-teal-700 dark:text-teal-400 mt-0.5 font-medium">
              Awaiting Cluster Head
            </div>
          </CardContent>
        </Card>

        {/* KPI 7: Approved */}
        <Card
          className="bg-surface border-emerald-300 dark:border-emerald-900/60 shadow-sm hover:shadow transition-shadow cursor-pointer hover:bg-emerald-50/40 dark:hover:bg-emerald-950/25"
          onClick={() => applyCrossFilter('status', 'APPROVED')}
          title="Click to isolate Approved requirements"
        >
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between text-emerald-700 dark:text-emerald-400 mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider">Approved</span>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </div>
            <div data-testid="kpi-approved" className="text-lg font-extrabold text-emerald-900 dark:text-emerald-300">
              {isError ? '—' : formatNumber(metrics.approvedCount)}
            </div>
            <div className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-0.5 font-medium">
              Fully approved
            </div>
          </CardContent>
        </Card>

        {/* KPI 8: Rejected */}
        <Card
          className="bg-surface border-rose-300 dark:border-rose-900/60 shadow-sm hover:shadow transition-shadow cursor-pointer hover:bg-rose-50/40 dark:hover:bg-rose-950/25"
          onClick={() => applyCrossFilter('status', 'REJECTED_BY_MANAGER')}
          title="Click to isolate Rejected requirements"
        >
          <CardContent className="p-3.5">
            <div className="flex items-center justify-between text-rose-700 dark:text-rose-400 mb-1">
              <span className="text-[11px] font-bold uppercase tracking-wider">Rejected</span>
              <XCircle className="h-4 w-4 text-rose-500" />
            </div>
            <div data-testid="kpi-rejected" className="text-lg font-extrabold text-rose-900 dark:text-rose-300">
              {isError ? '—' : formatNumber(metrics.rejectedCount)}
            </div>
            <div className="text-[10px] text-rose-700 dark:text-rose-400 mt-0.5 font-medium">
              Rejected in workflow
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── 4. Tabbed Analytics Experience ───────────────────────────────── */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="bg-surface p-1.5 rounded-xl border border-default shadow-sm flex items-center justify-between">
          <TabsList className="bg-surface-secondary border border-default/70 p-1 rounded-lg">
            <TabsTrigger
              value="overview"
              className="text-xs font-semibold px-3 py-1.5 text-secondary hover:text-primary data-[state=active]:bg-surface data-[state=active]:text-teal-600 dark:data-[state=active]:text-teal-400 data-[state=active]:shadow-sm transition-all"
            >
              <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
              Executive Overview
            </TabsTrigger>
            <TabsTrigger
              value="approvals"
              className="text-xs font-semibold px-3 py-1.5 text-secondary hover:text-primary data-[state=active]:bg-surface data-[state=active]:text-teal-600 dark:data-[state=active]:text-teal-400 data-[state=active]:shadow-sm transition-all"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              Approval & Workflow
            </TabsTrigger>
            <TabsTrigger
              value="costs"
              className="text-xs font-semibold px-3 py-1.5 text-secondary hover:text-primary data-[state=active]:bg-surface data-[state=active]:text-teal-600 dark:data-[state=active]:text-teal-400 data-[state=active]:shadow-sm transition-all"
            >
              <IndianRupee className="h-3.5 w-3.5 mr-1.5" />
              Project & Cost Analytics
            </TabsTrigger>
            <TabsTrigger
              value="workforce"
              className="text-xs font-semibold px-3 py-1.5 text-secondary hover:text-primary data-[state=active]:bg-surface data-[state=active]:text-teal-600 dark:data-[state=active]:text-teal-400 data-[state=active]:shadow-sm transition-all"
            >
              <Users className="h-3.5 w-3.5 mr-1.5" />
              Workforce Intelligence
            </TabsTrigger>
            <TabsTrigger
              value="table"
              className="text-xs font-semibold px-3 py-1.5 text-secondary hover:text-primary data-[state=active]:bg-surface data-[state=active]:text-teal-600 dark:data-[state=active]:text-teal-400 data-[state=active]:shadow-sm transition-all"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
              Detailed Data Grid ({metrics.totalRecords})
            </TabsTrigger>
          </TabsList>

          <span className="text-xs text-muted hidden md:inline pr-2">
            Click chart elements to cross-filter
          </span>
        </div>

        {/* ── TAB 1: EXECUTIVE OVERVIEW ─────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Chart 1: Project Labour Spend Ranking */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Labour Amount by Project</h3>
                    <p className="text-xs text-muted">Expenditure ranked by total planned cost (₹)</p>
                  </div>
                  <span className="text-xs font-semibold text-teal-700 dark:text-teal-300 bg-teal-500/10 px-2 py-0.5 rounded border border-teal-500/20">
                    {projectChartData.length} Projects
                  </span>
                </div>
                <div className="h-64 w-full">
                  {projectChartData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted">No project data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={projectChartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chartTheme.gridStroke} />
                        <XAxis type="number" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} textAnchor="middle" tick={{ fontSize: 11, fill: chartTheme.axisTick }} />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: chartTheme.axisLabel }} />
                        <Tooltip
                          formatter={(v: any) => [formatCurrency(Number(v)), 'Labour Amount']}
                          labelStyle={{ fontWeight: 'bold', color: chartTheme.axisLabel }}
                          contentStyle={chartTheme.tooltip}
                        />
                        <Bar
                          dataKey="amount"
                          fill="#0d9488"
                          radius={[0, 4, 4, 0]}
                          onClick={(entry) => {
                            const pObj = projects.find((p: any) => p.name === entry.name);
                            if (pObj) applyCrossFilter('projectId', pObj.id);
                          }}
                          className="cursor-pointer hover:opacity-85 transition-opacity"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Chart 2: Requirement Status Distribution */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Requirement Status Distribution</h3>
                    <p className="text-xs text-muted">Deduplicated unique requirement statuses</p>
                  </div>
                  <span className="text-xs font-semibold text-secondary bg-surface-secondary px-2 py-0.5 rounded border border-default">
                    {metrics.uniqueRequirements} Reqs
                  </span>
                </div>
                <div className="h-64 w-full flex items-center">
                  {statusDistributionData.length === 0 ? (
                    <div className="h-full w-full flex items-center justify-center text-xs text-muted">No status data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusDistributionData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={85}
                          paddingAngle={3}
                          onClick={(entry) => applyCrossFilter('status', entry.rawStatus)}
                          className="cursor-pointer"
                        >
                          {statusDistributionData.map((e, idx) => (
                            <Cell key={`cell-${idx}`} fill={e.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(val: any, name: any) => [`${val} Requirements (${((Number(val) / Math.max(1, metrics.uniqueRequirements)) * 100).toFixed(1)}%)`, name]}
                          contentStyle={chartTheme.tooltip}
                        />
                        <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11, color: chartTheme.axisTick }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Chart 3: Work Date Timeline Trend */}
            <Card className="border-default bg-surface shadow-sm lg:col-span-2">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Expenditure & Headcount Timeline</h3>
                    <p className="text-xs text-muted">Planned labour spend (₹) and headcount trend by work date</p>
                  </div>
                  <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-500/10 px-2 py-0.5 rounded border border-blue-500/20">
                    {timelineData.length} Work Dates
                  </span>
                </div>
                <div className="h-60 w-full">
                  {timelineData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted">No chronological timeline data available</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={timelineData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="tealGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartTheme.gridStroke} />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: chartTheme.axisTick }} tickFormatter={(d) => formatDate(d)} />
                        <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: chartTheme.axisTick }} />
                        <Tooltip
                          labelFormatter={(d) => `Date: ${formatDate(String(d))}`}
                          formatter={(v: any, name: any) => [
                            name === 'amount' ? formatCurrency(Number(v)) : `${v} workers`,
                            name === 'amount' ? 'Planned Spend' : 'Headcount',
                          ]}
                          contentStyle={chartTheme.tooltip}
                          labelStyle={{ color: chartTheme.axisLabel }}
                        />
                        <Area type="monotone" dataKey="amount" stroke="#0d9488" strokeWidth={2.5} fillOpacity={1} fill="url(#tealGradient)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Chart 4: Skill Level Distribution */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Skill Level Composition</h3>
                    <p className="text-xs text-muted">Headcount distribution</p>
                  </div>
                </div>
                <div className="h-60 w-full flex items-center">
                  {skillDistributionData.length === 0 ? (
                    <div className="h-full w-full flex items-center justify-center text-xs text-muted">No skill level data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={skillDistributionData}
                          dataKey="headcount"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={75}
                          innerRadius={45}
                          paddingAngle={3}
                          onClick={(entry) => applyCrossFilter('skillLevel', entry.rawSkill)}
                          className="cursor-pointer"
                        >
                          {skillDistributionData.map((e, idx) => (
                            <Cell key={`skill-${idx}`} fill={e.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(val: any, name: any) => [`${val} Workers (${((Number(val) / Math.max(1, metrics.totalHeadcount)) * 100).toFixed(1)}%)`, name]}
                          contentStyle={chartTheme.tooltip}
                        />
                        <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11, color: chartTheme.axisTick }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── TAB 2: APPROVAL & WORKFLOW ANALYTICS ────────────────────────── */}
        <TabsContent value="approvals" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Chart 5: PM vs CH Decision Stage Comparison */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Manager vs Cluster Head Decisions</h3>
                    <p className="text-xs text-muted">Side-by-side count of approval decisions by workflow level</p>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20">
                    2-Stage Governance
                  </span>
                </div>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={approvalComparisonData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartTheme.gridStroke} />
                      <XAxis dataKey="stage" tick={{ fontSize: 11, fill: chartTheme.axisLabel }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: chartTheme.axisTick }} />
                      <Tooltip contentStyle={chartTheme.tooltip} labelStyle={{ color: chartTheme.axisLabel }} />
                      <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.axisTick }} />
                      <Bar dataKey="PM" fill="#0d9488" name="Project Manager Stage" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="CH" fill="#3b82f6" name="Cluster Head Stage" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Chart 6: Pending Approvals by Project */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Pending Approvals by Project</h3>
                    <p className="text-xs text-muted">Requirements waiting for PM and CH action</p>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/20">
                    {metrics.pendingPmCount + metrics.pendingChCount} Total Pending
                  </span>
                </div>
                <div className="h-64 w-full">
                  {pendingByProjectData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted">
                      No pending approvals for the selected filters
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={pendingByProjectData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartTheme.gridStroke} />
                        <XAxis dataKey="project" tick={{ fontSize: 10, fill: chartTheme.axisLabel }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: chartTheme.axisTick }} />
                        <Tooltip contentStyle={chartTheme.tooltip} labelStyle={{ color: chartTheme.axisLabel }} />
                        <Legend wrapperStyle={{ fontSize: 11, color: chartTheme.axisTick }} />
                        <Bar dataKey="pendingPm" stackId="a" fill="#f59e0b" name="Awaiting PM Approval" />
                        <Bar dataKey="pendingCh" stackId="a" fill="#0d9488" name="Awaiting Cluster Head" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Rejection Diagnostics & Approval Workflow Health Card */}
          <Card className="border-default bg-surface shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-primary mb-1">Approval Governance & Workflow Pipeline</h3>
              <p className="text-xs text-muted mb-4">
                Detailed audit of items currently held in each stage of the departmental approval hierarchy
              </p>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl border border-default bg-surface-secondary/60">
                  <div className="text-xs font-bold text-muted uppercase">Draft Stage</div>
                  <div className="text-2xl font-bold text-primary mt-1">{metrics.draftCount}</div>
                  <p className="text-xs text-muted mt-1">Requisitions not yet submitted for review</p>
                </div>

                <div className="p-4 rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/20">
                  <div className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase">Manager Queue</div>
                  <div className="text-2xl font-bold text-amber-900 dark:text-amber-300 mt-1">{metrics.pendingPmCount}</div>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">Pending initial review by Site PM</p>
                </div>

                <div className="p-4 rounded-xl border border-teal-200 dark:border-teal-900/50 bg-teal-50/50 dark:bg-teal-950/20">
                  <div className="text-xs font-bold text-teal-700 dark:text-teal-400 uppercase">Cluster Head Queue</div>
                  <div className="text-2xl font-bold text-teal-900 dark:text-teal-300 mt-1">{metrics.pendingChCount}</div>
                  <p className="text-xs text-teal-700 dark:text-teal-400 mt-1">PM-approved, awaiting final sign-off</p>
                </div>

                <div className="p-4 rounded-xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-950/20">
                  <div className="text-xs font-bold text-rose-700 dark:text-rose-400 uppercase">Rejection Archive</div>
                  <div className="text-2xl font-bold text-rose-900 dark:text-rose-300 mt-1">{metrics.rejectedCount}</div>
                  <p className="text-xs text-rose-700 dark:text-rose-400 mt-1">
                    {metrics.rejectedPmCount} by PM · {metrics.rejectedChCount} by Cluster Head
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TAB 3: PROJECT & COST ANALYTICS ────────────────────────────── */}
        <TabsContent value="costs" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Chart 7: Top Activities by Expenditure */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Top Activities by Expenditure</h3>
                    <p className="text-xs text-muted">Highest planned expenditure activities (₹)</p>
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-teal-500/10 text-teal-700 dark:text-teal-300 border border-teal-500/20">
                    Activity Breakdown
                  </span>
                </div>
                <div className="h-64 w-full">
                  {activitySpendData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted">No activity expenditure recorded</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={activitySpendData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chartTheme.gridStroke} />
                        <XAxis type="number" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: chartTheme.axisTick }} />
                        <YAxis type="category" dataKey="activity" width={110} tick={{ fontSize: 10, fill: chartTheme.axisLabel }} />
                        <Tooltip
                          formatter={(v: any) => [formatCurrency(Number(v)), 'Planned Amount']}
                          contentStyle={chartTheme.tooltip}
                          labelStyle={{ color: chartTheme.axisLabel }}
                        />
                        <Bar
                          dataKey="amount"
                          fill="#3b82f6"
                          radius={[0, 4, 4, 0]}
                          onClick={(entry) => {
                            const aObj = activities.find((a: any) => a.name === entry.activity);
                            if (aObj) applyCrossFilter('activityId', aObj.id);
                          }}
                          className="cursor-pointer hover:opacity-85"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Chart 8: Headcount by Project */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Requested Headcount by Project</h3>
                    <p className="text-xs text-muted">Total manpower deployed across projects</p>
                  </div>
                </div>
                <div className="h-64 w-full">
                  {projectChartData.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted">No headcount data</div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={projectChartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chartTheme.gridStroke} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: chartTheme.axisTick }} />
                        <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10, fill: chartTheme.axisLabel }} />
                        <Tooltip
                          formatter={(v: any) => [`${formatNumber(Number(v))} Workers`, 'Headcount']}
                          contentStyle={chartTheme.tooltip}
                          labelStyle={{ color: chartTheme.axisLabel }}
                        />
                        <Bar
                          dataKey="headcount"
                          fill="#0d9488"
                          radius={[0, 4, 4, 0]}
                          onClick={(entry) => {
                            const pObj = projects.find((p: any) => p.name === entry.name);
                            if (pObj) applyCrossFilter('projectId', pObj.id);
                          }}
                          className="cursor-pointer hover:opacity-85"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── TAB 4: WORKFORCE ANALYTICS ─────────────────────────────────── */}
        <TabsContent value="workforce" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Chart 9: Headcount by Shift */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Deployment by Shift</h3>
                    <p className="text-xs text-muted">Headcount distribution by shift timing</p>
                  </div>
                </div>
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={shiftData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartTheme.gridStroke} />
                      <XAxis dataKey="shift" tick={{ fontSize: 10, fill: chartTheme.axisLabel }} />
                      <YAxis tick={{ fontSize: 10, fill: chartTheme.axisTick }} />
                      <Tooltip formatter={(v: any) => [`${v} Workers`, 'Headcount']} contentStyle={chartTheme.tooltip} labelStyle={{ color: chartTheme.axisLabel }} />
                      <Bar dataKey="headcount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Chart 10: Priority Breakdown */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Requirement Priority</h3>
                    <p className="text-xs text-muted">Urgency level of requests</p>
                  </div>
                </div>
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={priorityData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartTheme.gridStroke} />
                      <XAxis dataKey="priority" tick={{ fontSize: 10, fill: chartTheme.axisLabel }} />
                      <YAxis tick={{ fontSize: 10, fill: chartTheme.axisTick }} />
                      <Tooltip formatter={(v: any) => [`${v} Requirements`, 'Count']} contentStyle={chartTheme.tooltip} labelStyle={{ color: chartTheme.axisLabel }} />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Card 11: Skill Breakdown Matrix */}
            <Card className="border-default bg-surface shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-primary">Skill Level Economics</h3>
                    <p className="text-xs text-muted">Headcount vs expenditure per skill tier</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {skillDistributionData.map((s) => (
                    <div key={s.rawSkill} className="p-3 rounded-lg border border-default bg-surface-secondary/60">
                      <div className="flex items-center justify-between text-xs font-semibold">
                        <span className="flex items-center gap-1.5 text-primary">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                          {s.name}
                        </span>
                        <span className="text-primary font-bold">{formatCurrency(s.amount)}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-muted mt-1">
                        <span>{s.headcount} requested workers</span>
                        <span>Avg ₹{s.headcount > 0 ? (s.amount / s.headcount).toFixed(0) : 0}/worker</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── TAB 5: DETAILED DATA GRID ─────────────────────────────────── */}
        <TabsContent value="table" className="space-y-4">
          <Card className="border-default bg-surface shadow-sm overflow-hidden">
            <CardContent className="p-5">
              {/* Table Controls Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted" />
                  <Input
                    placeholder="Search reference, project, activity, PM, status..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPage(1);
                    }}
                    className="pl-9 h-9 text-xs border-default bg-surface-secondary text-primary placeholder:text-muted focus:bg-surface"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-2.5 text-muted hover:text-primary"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Column Visibility Dropdown */}
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowColumnPicker((prev) => !prev)}
                      className="text-xs h-9 border-default bg-surface-secondary text-secondary hover:text-primary"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5 text-secondary" />
                      Columns ({visibleColumns.size}/{ALL_COLUMNS.length})
                    </Button>

                    {showColumnPicker && (
                      <div className="absolute right-0 mt-1 w-64 p-3 bg-surface border border-default rounded-xl shadow-xl z-50 text-xs space-y-2">
                        <div className="flex items-center justify-between font-bold text-primary pb-1 border-b border-default">
                          <span>Visible Columns</span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setVisibleColumns(new Set(ALL_COLUMNS.map((c) => c.key)))}
                              className="text-[10px] text-teal-600 dark:text-teal-400 hover:underline font-semibold"
                            >
                              All
                            </button>
                            <button
                              onClick={() => setVisibleColumns(new Set(ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)))}
                              className="text-[10px] text-muted hover:text-primary hover:underline font-semibold"
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                        <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
                          {ALL_COLUMNS.map((col) => (
                            <label key={col.key} className="flex items-center gap-2 cursor-pointer hover:bg-surface-secondary p-1 rounded text-primary">
                              <input
                                type="checkbox"
                                checked={visibleColumns.has(col.key)}
                                onChange={(e) => {
                                  setVisibleColumns((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(col.key);
                                    else next.delete(col.key);
                                    return next;
                                  });
                                }}
                                className="rounded text-teal-600 focus:ring-teal-500 bg-surface border-default"
                              />
                              <span className="truncate">{col.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Rows per page */}
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      setPageSize(Number(v));
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-9 w-24 text-xs border-default bg-surface-secondary text-primary">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 / page</SelectItem>
                      <SelectItem value="25">25 / page</SelectItem>
                      <SelectItem value="50">50 / page</SelectItem>
                      <SelectItem value="100">100 / page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Table Data Container */}
              <div className="border border-default rounded-lg overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-surface-secondary text-secondary border-b border-default sticky top-0 uppercase tracking-wider text-[10px] font-bold">
                    <tr>
                      {ALL_COLUMNS.filter((c) => visibleColumns.has(c.key)).map((col) => (
                        <th
                          key={col.key}
                          onClick={() => {
                            if (sortKey === col.key) {
                              setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                            } else {
                              setSortKey(col.key);
                              setSortDir('asc');
                            }
                          }}
                          className="px-3 py-2.5 font-bold cursor-pointer hover:bg-surface-hover text-secondary hover:text-primary whitespace-nowrap transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <span>{col.label}</span>
                            {sortKey === col.key && (
                              <ArrowUpDown className="h-3 w-3 text-brand-600 dark:text-brand-400" />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-default/50">
                    {isLoading ? (
                      <tr>
                        <td colSpan={visibleColumns.size} className="p-8 text-center text-muted">
                          <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-teal-600 dark:text-teal-400" />
                          Loading authoritative dataset...
                        </td>
                      </tr>
                    ) : paginatedRows.length === 0 ? (
                      <tr>
                        <td colSpan={visibleColumns.size} className="p-8 text-center text-muted">
                          No matching records found for the selected slicers and search criteria.
                        </td>
                      </tr>
                    ) : (
                      paginatedRows.map((row, idx) => (
                        <tr
                          key={row.row_id || idx}
                          onClick={() => {
                            if (row.requirement_id) {
                              navigate(`/requirements/${row.requirement_id}`);
                            }
                          }}
                          className="hover:bg-surface-secondary/70 transition-colors cursor-pointer group border-b border-default/40"
                        >
                          {ALL_COLUMNS.filter((c) => visibleColumns.has(c.key)).map((col) => {
                            const val = row[col.key];

                            // Formatting
                            let content: React.ReactNode = val;
                            if (val === null || val === undefined || val === '' || val === '-' || val === '—' || val === 'â€”') {
                              content = <span className="text-muted/60">-</span>;
                            } else if (col.key === 'requirement_number') {
                              content = (
                                <span className="font-semibold text-teal-600 dark:text-teal-400 group-hover:underline flex items-center gap-1">
                                  {val}
                                  <ExternalLink className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </span>
                              );
                            } else if (col.key === 'planned_amount' || col.key === 'planned_rate') {
                              content = <span className="font-medium text-primary">{formatCurrency(Number(val) || 0)}</span>;
                            } else if (col.key === 'required_headcount' || col.key === 'quantity' || col.key === 'planned_hours') {
                              content = <span className="font-medium text-primary">{formatNumber(Number(val) || 0)}</span>;
                            } else if (col.key === 'work_date') {
                              content = formatDate(val);
                            } else if (col.key === 'status') {
                              const st = String(val);
                              const isApproved = st === 'APPROVED';
                              const isPending = st.includes('PENDING') || st === 'MANAGER_APPROVED';
                              const isRejected = st.includes('REJECTED');
                              const badgeStyle = isApproved
                                ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60'
                                : isPending
                                ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/60'
                                : isRejected
                                ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/60'
                                : 'bg-surface-secondary text-secondary border-default';
                              content = (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${badgeStyle}`}>
                                  {st.replace(/_/g, ' ')}
                                </span>
                              );
                            } else if (col.key === 'manager_decision' || col.key === 'cluster_decision') {
                              const d = String(val);
                              if (d === 'APPROVED') {
                                content = <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-semibold"><CheckCircle2 className="h-3 w-3" /> Approved</span>;
                              } else if (d === 'REJECTED') {
                                content = <span className="inline-flex items-center gap-1 text-rose-700 dark:text-rose-400 font-semibold"><XCircle className="h-3 w-3" /> Rejected</span>;
                              } else if (d === 'PENDING') {
                                content = <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-semibold"><Clock3 className="h-3 w-3" /> Pending</span>;
                              } else {
                                content = <span className="text-muted/60">-</span>;
                              }
                            } else if (col.key === 'skill_level') {
                              const s = String(val);
                              content = s === 'SEMI_SKILLED' ? 'Semi-Skilled' : s === 'SKILLED' ? 'Skilled' : s === 'UNSKILLED' ? 'Unskilled' : s;
                            }

                            return (
                              <td key={col.key} className="px-3 py-2.5 whitespace-nowrap text-secondary">
                                {content}
                              </td>
                            );
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                  {/* Totals Summary Footer */}
                  {filteredTableRows.length > 0 && (
                    <tfoot className="bg-surface-secondary/95 font-bold text-primary border-t-2 border-default text-[11px]">
                      <tr>
                        {ALL_COLUMNS.filter((c) => visibleColumns.has(c.key)).map((col, idx) => {
                          if (idx === 0) return <td key={col.key} className="px-3 py-2.5">Totals ({filteredTableRows.length} Rows)</td>;
                          if (col.key === 'required_headcount') {
                            const totalHc = filteredTableRows.reduce((acc, r) => acc + (Number(r.required_headcount) || 0), 0);
                            return <td key={col.key} className="px-3 py-2.5 text-blue-600 dark:text-blue-400">{formatNumber(totalHc)}</td>;
                          }
                          if (col.key === 'planned_hours') {
                            const totalHrs = filteredTableRows.reduce((acc, r) => acc + (Number(r.planned_hours) || 0), 0);
                            return <td key={col.key} className="px-3 py-2.5">{formatNumber(totalHrs)} hrs</td>;
                          }
                          if (col.key === 'planned_amount') {
                            const totalAmt = filteredTableRows.reduce((acc, r) => acc + (Number(r.planned_amount) || 0), 0);
                            return <td key={col.key} className="px-3 py-2.5 text-teal-600 dark:text-teal-400">{formatCurrency(totalAmt)}</td>;
                          }
                          return <td key={col.key} className="px-3 py-2.5 text-muted/60">-</td>;
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {/* Pagination Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4 text-xs text-muted">
                <div>
                  Showing{' '}
                  <span className="font-semibold text-primary">
                    {filteredTableRows.length === 0 ? 0 : (page - 1) * pageSize + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-semibold text-primary">
                    {Math.min(page * pageSize, filteredTableRows.length)}
                  </span>{' '}
                  of <span className="font-semibold text-primary">{filteredTableRows.length}</span> entries
                </div>

                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="h-8 px-2.5 text-xs border-default bg-surface-secondary text-secondary hover:text-primary"
                  >
                    <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                    Previous
                  </Button>

                  <span className="px-3 py-1 font-semibold text-secondary bg-surface-secondary border border-default rounded-md">
                    {page} / {totalTablePages}
                  </span>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalTablePages, p + 1))}
                    disabled={page >= totalTablePages}
                    className="h-8 px-2.5 text-xs border-default bg-surface-secondary text-secondary hover:text-primary"
                  >
                    Next
                    <ChevronRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
